import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Chip,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as IconDelete } from '../../../../../assets/icons/icon-cross-circle-white.svg';
import useBulkCreatePrivateDrivers from '../../../../../hooks/driver/useBulkCreatePrivateDrivers';
import { removeSpecialChars } from '../../../../../services/mask';
import ButtonComponent from '../../../../atoms/button';
import Input from '../../../../atoms/input';
import Typography from '../../../../atoms/typography';
import schema from './schema';
import colors from '../../../../../assets/styles/colors';
import commons from '../../../../../assets/styles/commons';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/icon-info.svg';
import { useHistory } from 'react-router-dom';
import ModalCustom from '../../../../atoms/modalCustom';

interface IDriverForm {
  id: string;
  username: string;
  cpf: string;
  phoneNumber: string;
}

const actualTabNumber = '4';

const useStyles = makeStyles(() =>
  createStyles({
    chip: {
      backgroundColor: colors.neutral.superLightGray,
      color: colors.secondary.intenseNavy,
      border: '1px solid #8F9BB3',
      borderRadius: '8px',
      padding: 5,
      fontFamily: commons.fontFamily,
      fontSize: 14,
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      fontVariant: 'lining-nums',
    },
  }),
);

const FormCreatePrivateDrivers: React.FC<any> = ({ onClick }: any) => {
  const classes = useStyles();
  const history = useHistory();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const { execute, loading } = useBulkCreatePrivateDrivers();
  const {
    control,
    formState: { isValid, isDirty },
    getValues,
  } = useForm<{
    privateDrivers: IDriverForm[];
  }>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      privateDrivers: [
        {
          id: uuidv4(),
          username: '',
          cpf: '',
          phoneNumber: '',
        },
      ],
    },
  });
  const { append, fields, remove } = useFieldArray({
    control,
    name: 'privateDrivers',
  });

  const addDriverUserToForm = () => {
    append({
      id: uuidv4(),
      username: '',
      cpf: '',
      phoneNumber: '',
    });
  };

  const registerDrivers = async () => {
    const formattedPrivateDrivers = getValues('privateDrivers').map(driver => ({
      cpf: removeSpecialChars(driver.cpf),
      username: driver.username,
      phoneNumber: removeSpecialChars(driver.phoneNumber),
    }));

    await execute(formattedPrivateDrivers, () => {
      history.goBack();
    });
  };

  const deleteDistributorUser = (indexToRemove: number) => {
    remove(indexToRemove);
  };

  const cancelOperation = () => {
    history.goBack();
  };

  const decideToCancelOperation = () => {
    if (!isDirty) cancelOperation();
    else setOpenCancelModal(true);
  };

  return (
    <Box style={{ display: 'flex', gap: 20, flexDirection: 'column' }}>
      <Typography fontWeight="700" color="secondary">
        Adicionar motoristas
      </Typography>
      <Chip
        label="É necessário adicionar previamente os motoristas para que eles possam se cadastrar no aplicativo como funcionários da distribuidora."
        icon={<InfoIcon />}
        className={classes.chip}
      />

      <Box>
        {fields.map((field, index) => {
          return (
            <Grid container spacing={2} key={field.id}>
              <Grid item xs={3}>
                <Input
                  size="small"
                  label="CPF"
                  placeholder="000.000.000-00"
                  mask="999.999.999-99"
                  control={control}
                  name={`privateDrivers.${index}.cpf`}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  name={`privateDrivers.${index}.username`}
                  control={control}
                  size="small"
                  label="Nome"
                  placeholder="João da Silva"
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  name={`privateDrivers.${index}.phoneNumber`}
                  control={control}
                  size="small"
                  label="Celular"
                  placeholder="(00)00000-0000"
                  mask="(99)99999-9999"
                />
              </Grid>

              <Grid item xs={1}>
                <ButtonComponent
                  theme="red"
                  mt={6}
                  justifyContent="none"
                  disabled={fields.length <= 1}
                  onClick={() => deleteDistributorUser(index)}
                >
                  <IconButton>
                    <IconDelete />
                  </IconButton>
                </ButtonComponent>
              </Grid>
            </Grid>
          );
        })}
        <br />
        {fields.length < 5 && (
          <ButtonComponent
            theme="primary"
            onClick={addDriverUserToForm}
            size="small"
            style={{ height: '50%' }}
            disabled={!isValid}
          >
            <FontAwesomeIcon
              icon={solid('plus')}
              size="sm"
              color="#fff"
              style={{ marginRight: 5 }}
            />
            Adicionar motorista
          </ButtonComponent>
        )}
      </Box>

      <Grid container justifyContent="space-between">
        <ButtonComponent
          theme="outline-primary"
          justifyContent="flex-start"
          flex={1}
          onClick={decideToCancelOperation}
        >
          Cancelar
        </ButtonComponent>
        <ButtonComponent
          onClick={registerDrivers}
          theme="primary"
          justifyContent="flex-end"
          flex={1}
          disabled={!isValid}
        >
          Salvar novos motoristas
        </ButtonComponent>
      </Grid>

      <ModalCustom
        open={openCancelModal}
        onCancelClick={() => setOpenCancelModal(false)}
        onClickPublish={cancelOperation}
        ButtonOutlinePrimary="Cancelar"
        ButtonPrimary="Confirmar"
        alignCenter={true}
        hideCloseIcon={true}
        label="Deseja descartar os motoristas que estavam sendo cadastrados nesse formulário?"
        boxStyle={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          gap: 2,
        }}
      />
    </Box>
  );
};

export default FormCreatePrivateDrivers;
